import { Suspense, useState, useEffect } from "react";
import { Navigate, useLocation, useRoutes } from "react-router-dom";
// layouts
// import ContactCenterLayout from "../layouts/ContactCenter/ContactCenterLayout.jsx";
import ContactCenterLayout from "../layouts/ContactCenter/ContactCenterLayout.jsx";
import OnlineLayout from "../layouts/Online/OnlineLayout.jsx";
import NoHeaderLayout from "../layouts/NoHeaderLayout.jsx";
import StaticPageLayout from "../layouts/StaticPageLayout.jsx";

// guards
import AuthGuard from "../guards/AuthGuard.jsx";
import CCPageGuard from "../guards/CCPageGuard.jsx";
import GuestGuard from "../guards/GuestGuard.jsx";
import PageGuard from "../guards/PageGuard.jsx";

// config
import { PATH_AFTER_LOGIN, PATH_AFTER_LOGIN_CC } from "../config.js";
import { pageOrder, pageOrderCC } from "../config/pageInfo/pageOrder.js";
import {
	PATH_CONTACT_CENTER,
	PATH_CONTACT_CENTER_SUB,
	PATH_ONLINE,
	PATH_ONLINE_SUB,
	PATH_SHARED_SUB,
} from "./paths";

// components
import LoadingScreen from "../components/LoadingScreen.jsx";
import ContactCenterAuthGuard from "../guards/ContactCenterAuthGuard.jsx";
import JWTPageGuard from "../guards/JWTPageGuard.jsx";

// Pages
import LoginPage from "../pages/auth/Login";
import AcceptAndProceedPage from "../pages/online/AcceptAndProceed";
import PaymentDetailsOnlinePage from "../pages/online/PaymentDetailsOnline";
import PolicyOutcomeOnlinePage from "../pages/online/PolicyOutcomeOnline.jsx";
import OpeningScriptPage from "../pages/contactCenter/OpeningScript";
import PaymentDetailsCCPage from "../pages/contactCenter/PaymentDetailsCC.jsx";
import PolicyOutcomeCCPage from "../pages/contactCenter/PolicyOutcomeCC";
import UpgradeSaveConfirmationPage from "../pages/contactCenter/UpgradeSaveConfirmation";
import ProcessingLandingPageCCPage from "../pages/contactCenter/ProcessingLandingPageCC";
import RetrieveQuotePageCCPage from "../pages/contactCenter/RetrieveQuotePageCC";
import YourDetailsContactPage from "../pages/shared/prequalification/YourDetailsContact";
import YourDetailsPersonalPage from "../pages/shared/prequalification/YourDetailsPersonal";
import SelectCoverPage from "../pages/shared/prequalification/SelectCover";
import QuoteSummaryPage from "../pages/shared/prequalification/QuoteSummary";
import PaymentContactDetailsPage from "../pages/shared/paymentPath/PaymentContactDetails";
import PolicyPurchaseConfirmationPage from "../pages/shared/paymentPath/PolicyPurchaseConfirmation";
import AboutYouDisclosurePage from "../pages/shared/underwriting/AboutYouDisclosure";
import AboutYouQuestionsPage from "../pages/shared/underwriting/AboutYouQuestions";
import PolicyOutcomeDeclinePage from "../pages/shared/policyOutcome/PolicyOutcomeDecline";
import ReviewApplicationPage from "../pages/shared/underwriting/ReviewApplication";
import RetrieveLoadingPage from "../pages/shared/retrieveQuote/RetrieveLoading";
import RetrieveOutcomeDeclinePage from "../pages/shared/retrieveOutcome/RetrieveOutcomeDecline";
import RetrieveOutcomeExpiredPage from "../pages/shared/retrieveOutcome/RetrieveOutcomeExpired";
import RetrieveOutcomeBoundPage from "../pages/shared/retrieveOutcome/RetrieveOutcomeBound";
import RetrieveQuoteOnlinePage from "../pages/online/RetrieveQuoteOnline";
import Page404Page from "../pages/Page404";
import LandingPagePage from "../pages/LandingPage";
import LifeInfoAndDutyPage from "../pages/LifeInfoAndDuty";
import MaintenanceInfo from "../pages/maintenance/MaintenanceInfo.jsx";
import AlreadyDeclinedApplication from "../pages/shared/policyOutcome/AlreadyDeclinedApplication.jsx";
// ----------------------------------------------------------------------
import { useCheckAppStatus } from "../hooks/dataHooks/useCheckAppStatus.js";

const Loadable = (Component) => (props) => {
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const { pathname } = useLocation();
	return (
		<Suspense
			fallback={<LoadingScreen isOnline={pathname.includes("/online")} />}
		>
			<Component {...props} />
		</Suspense>
	);
};

export default function Router() {
	const rootRedirect = "true" === import.meta.env.VITE_ROOT_REDIRECT;
	const [maintenanceMode, setMaintenanceMode] = useState(false);
	const { data } = useCheckAppStatus();

	useEffect(() => {
		if (!data) {
			return;
		}
		setMaintenanceMode("Maintenance" === data.appStatus);
	}, [data]);

	return useRoutes(
		maintenanceMode
			? [
					{
						path: "/",
						element: <StaticPageLayout />,
						children: [{ element: <MaintenanceInfoPage />, index: true }],
					},
					{ path: "*", element: <Navigate to="/" replace /> },
				]
			: [
					{
						path: "auth",
						element: (
							<GuestGuard>
								<Login />
							</GuestGuard>
						),
						children: [
							{
								path: "login",
								element: (
									<GuestGuard>
										<Login />
									</GuestGuard>
								),
							},
						],
					},
					// Online Routes
					{
						path: PATH_ONLINE_SUB.root,
						element: (
							<AuthGuard>
								<OnlineLayout />
							</AuthGuard>
						),
						children: [
							{
								element: <Navigate to={PATH_AFTER_LOGIN} replace />,
								index: true,
							},
							{
								path: PATH_ONLINE_SUB.accept,
								element: (
									<PageGuard>
										<AcceptAndProceed pageIndex={pageOrder.ACCEPT} />
									</PageGuard>
								),
							},
							{
								path: PATH_ONLINE_SUB.personal,
								element: (
									<PageGuard>
										<YourDetailsPersonal pageIndex={pageOrder.PERSONAL} />
									</PageGuard>
								),
							},
							{
								path: PATH_ONLINE_SUB.contact,
								element: (
									<PageGuard>
										<YourDetailsContact pageIndex={pageOrder.CONTACT} />
									</PageGuard>
								),
							},
							{
								path: PATH_ONLINE_SUB.cover,
								element: (
									<PageGuard>
										<SelectCover pageIndex={pageOrder.COVER} />
									</PageGuard>
								),
							},
							{
								path: PATH_ONLINE_SUB.quote,
								element: (
									<PageGuard>
										<QuoteSummary pageIndex={pageOrder.QUOTE} />
									</PageGuard>
								),
							},
							{
								path: PATH_ONLINE_SUB.aboutYouDisclosure,
								element: (
									<PageGuard>
										<AboutYouDisclosure
											pageIndex={pageOrder.ABOUT_YOU_DISCLOSURE}
										/>
									</PageGuard>
								),
							},
							{
								path: PATH_ONLINE_SUB.aboutYouQuestions,
								element: (
									<PageGuard>
										<AboutYouQuestions
											pageIndex={pageOrder.ABOUT_YOU_QUESTIONS}
										/>
									</PageGuard>
								),
							},
							{
								path: PATH_ONLINE_SUB.aboutYourReview,
								element: (
									<PageGuard>
										<ReviewApplication
											pageIndex={pageOrder.REVIEW_APPLICATION}
										/>
									</PageGuard>
								),
							},
							{
								path: PATH_ONLINE_SUB.ppOnlinePolicyOutcome,
								element: (
									<PageGuard>
										<AuthGuard>
											<PolicyOutcomeOnline
												pageIndex={pageOrder.PP_ONLINE_POLICY_OUTCOME}
											/>
										</AuthGuard>
									</PageGuard>
								),
							},
							{
								path: PATH_ONLINE_SUB.ppPolicyOutcomeDecline,
								element: (
									<PageGuard>
										<PolicyOutcomeDecline
											pageIndex={pageOrder.PP_POLICY_OUTCOME_DECLINE}
										/>
									</PageGuard>
								),
							},
							{
								path: PATH_ONLINE_SUB.ppContact,
								element: (
									<PageGuard>
										<AuthGuard>
											<PaymentContactDetails pageIndex={pageOrder.PP_CONTACT} />
										</AuthGuard>
									</PageGuard>
								),
							},
							{
								path: PATH_ONLINE_SUB.ppOnlinePayment,
								element: (
									<PageGuard>
										<AuthGuard>
											<PaymentDetailsOnline
												pageIndex={pageOrder.PP_ONLINE_PAYMENT}
											/>
										</AuthGuard>
									</PageGuard>
								),
							},
							{
								path: PATH_ONLINE_SUB.ppConfirmation,
								element: (
									<PageGuard>
										<AuthGuard>
											<PolicyPurchaseConfirmation
												pageIndex={pageOrder.PP_CONFIRMATION}
											/>
										</AuthGuard>
									</PageGuard>
								),
							},
							{
								path: PATH_ONLINE_SUB.retrieveQuoteLoading,
								element: (
									<PageGuard>
										<RetrieveLoading
											pageIndex={pageOrder.RETRIEVE_QUOTE_LOADING}
										/>
									</PageGuard>
								),
							},
							{
								path: PATH_ONLINE_SUB.retrieveDecline,
								element: (
									<PageGuard>
										<RetrieveDecline pageIndex={pageOrder.RETRIEVE_DECLINE} />
									</PageGuard>
								),
							},
							{
								path: PATH_ONLINE_SUB.retrieveReferredUW,
								element: (
									<PageGuard>
										<RetrieveReferredUW
											pageIndex={pageOrder.RETRIEVE_REFERRED_UW}
										/>
									</PageGuard>
								),
							},
							{
								path: PATH_ONLINE_SUB.retrieveExpired,
								element: (
									<PageGuard>
										<RetrieveExpired pageIndex={pageOrder.RETRIEVE_EXPIRED} />
									</PageGuard>
								),
							},
							{
								path: PATH_ONLINE_SUB.retrieveBound,
								element: (
									<PageGuard>
										<RetrieveBound pageIndex={pageOrder.RETRIEVE_BOUND} />
									</PageGuard>
								),
							},
							{
								path: PATH_ONLINE_SUB.applicationAlreadyDeclined,
								element: (
									<PageGuard>
										<ApplicationAlreadyDeclined
											pageIndex={pageOrder.APPLICATION_ALREADY_DECLINED}
										/>
									</PageGuard>
								),
							},
						],
					},
					//** Contact Center Routes  */
					{
						path: "contactCenter",
						element: (
							<ContactCenterAuthGuard>
								<ContactCenterLayout />
							</ContactCenterAuthGuard>
						),
						children: [
							{
								element: <Navigate to={PATH_AFTER_LOGIN_CC} replace />,
								index: true,
							},
							{
								path: PATH_CONTACT_CENTER_SUB.openingScript,
								element: (
									<CCPageGuard>
										<OpeningScript pageIndex={pageOrderCC.SCRIPT} />
									</CCPageGuard>
								),
							},
							{
								path: PATH_CONTACT_CENTER_SUB.personal,
								element: (
									<CCPageGuard>
										<YourDetailsPersonal pageIndex={pageOrderCC.CONTACT} />
									</CCPageGuard>
								),
							},
							{
								path: PATH_CONTACT_CENTER_SUB.contact,
								element: (
									<CCPageGuard>
										<YourDetailsContact pageIndex={pageOrderCC.PERSONAL} />
									</CCPageGuard>
								),
							},
							{
								path: PATH_CONTACT_CENTER_SUB.cover,
								element: (
									<CCPageGuard>
										<SelectCover pageIndex={pageOrderCC.COVER} />
									</CCPageGuard>
								),
							},
							{
								path: PATH_CONTACT_CENTER_SUB.quote,
								element: (
									<CCPageGuard>
										<QuoteSummary pageIndex={pageOrderCC.QUOTE} />
									</CCPageGuard>
								),
							},
							{
								path: PATH_CONTACT_CENTER_SUB.aboutYouDisclosure,
								element: (
									<JWTPageGuard>
										<AboutYouDisclosure
											pageIndex={pageOrderCC.ABOUT_YOU_DISCLOSURE}
										/>
									</JWTPageGuard>
								),
							},
							{
								path: PATH_CONTACT_CENTER_SUB.aboutYouQuestions,
								element: (
									<JWTPageGuard>
										<AboutYouQuestions
											pageIndex={pageOrderCC.ABOUT_YOU_QUESTIONS}
										/>
									</JWTPageGuard>
								),
							},
							{
								path: PATH_CONTACT_CENTER_SUB.aboutYourReview,
								element: (
									<JWTPageGuard>
										<ReviewApplication
											pageIndex={pageOrderCC.REVIEW_APPLICATION}
										/>
									</JWTPageGuard>
								),
							},
							{
								path: PATH_CONTACT_CENTER_SUB.ppPolicyOutcomeDecline,
								element: (
									<JWTPageGuard>
										<PolicyOutcomeDecline
											pageIndex={pageOrderCC.PP_POLICY_OUTCOME_DECLINE}
										/>
									</JWTPageGuard>
								),
							},
							{
								path: PATH_CONTACT_CENTER_SUB.ppContact,
								element: (
									<JWTPageGuard>
										<AuthGuard>
											<PaymentContactDetails
												pageIndex={pageOrderCC.PP_CONTACT}
											/>
										</AuthGuard>
									</JWTPageGuard>
								),
							},
							{
								path: PATH_CONTACT_CENTER_SUB.ppCCPayment,
								element: (
									<JWTPageGuard>
										<AuthGuard>
											<PaymentDetailsCC pageIndex={pageOrderCC.PP_CC_PAYMENT} />
										</AuthGuard>
									</JWTPageGuard>
								),
							},
							{
								path: PATH_CONTACT_CENTER_SUB.ppConfirmation,
								element: (
									<JWTPageGuard>
										<AuthGuard>
											<PolicyPurchaseConfirmation
												pageIndex={pageOrderCC.PP_CONFIRMATION}
											/>
										</AuthGuard>
									</JWTPageGuard>
								),
							},
							{
								path: PATH_CONTACT_CENTER_SUB.ppCCPolicyOutcome,
								element: (
									<JWTPageGuard>
										<AuthGuard>
											<PolicyOutcomeCC
												pageIndex={pageOrderCC.PP_CC_POLICY_OUTCOME}
											/>
										</AuthGuard>
									</JWTPageGuard>
								),
							},
							{
								path: PATH_CONTACT_CENTER_SUB.upgradeSaveConfirmation,
								element: (
									<CCPageGuard>
										<AuthGuard>
											<UpgradeSaveConfirmation />
										</AuthGuard>
									</CCPageGuard>
								),
							},
							{
								path: PATH_CONTACT_CENTER_SUB.retrieveQuoteLoading,
								element: (
									<CCPageGuard>
										<RetrieveLoading
											pageIndex={pageOrderCC.RETRIEVE_QUOTE_LOADING}
										/>
									</CCPageGuard>
								),
							},
							{
								path: PATH_CONTACT_CENTER_SUB.retrieveDecline,
								element: (
									<JWTPageGuard>
										<RetrieveDecline pageIndex={pageOrderCC.RETRIEVE_DECLINE} />
									</JWTPageGuard>
								),
							},
							{
								path: PATH_CONTACT_CENTER_SUB.retrieveReferredUW,
								element: (
									<JWTPageGuard>
										<RetrieveReferredUW
											pageIndex={pageOrderCC.RETRIEVE_REFERRED_UW}
										/>
									</JWTPageGuard>
								),
							},
							{
								path: PATH_CONTACT_CENTER_SUB.retrieveExpired,
								element: (
									<JWTPageGuard>
										<RetrieveExpired pageIndex={pageOrderCC.RETRIEVE_EXPIRED} />
									</JWTPageGuard>
								),
							},
							{
								path: PATH_CONTACT_CENTER_SUB.retrieveBound,
								element: (
									<JWTPageGuard>
										<RetrieveBound pageIndex={pageOrderCC.RETRIEVE_BOUND} />
									</JWTPageGuard>
								),
							},
							{
								path: PATH_ONLINE_SUB.applicationAlreadyDeclined,
								element: (
									<CCPageGuard>
										<ApplicationAlreadyDeclined
											pageIndex={pageOrderCC.APPLICATION_ALREADY_DECLINED}
										/>
									</CCPageGuard>
								),
							},
						],
					},
					{
						path: PATH_SHARED_SUB.infoAndDuty,
						element: <StaticPageLayout />,
						children: [{ element: <LifeInfoAndDuty />, index: true }],
					},
					{
						path: "/",
						...(rootRedirect
							? {
									element: <Navigate to={PATH_ONLINE_SUB.root} replace />,
								}
							: {
									element: <NoHeaderLayout />,
									children: [{ element: <LandingPage />, index: true }],
								}),
					},

					// CC landing page to start or retrieve quote
					{
						path: PATH_CONTACT_CENTER.landingPage,
						element: <StaticPageLayout />,
						children: [
							{
								element: (
									<ContactCenterAuthGuard>
										<ProcessingLandingPageCC />
									</ContactCenterAuthGuard>
								),
								index: true,
							},
						],
					},
					// CC retrieve quote page
					{
						path: PATH_CONTACT_CENTER.retrieveQuote,
						element: <StaticPageLayout />,
						children: [
							{
								element: (
									<ContactCenterAuthGuard>
										<RetrieveQuotePageCC />
									</ContactCenterAuthGuard>
								),
								index: true,
							},
						],
					},
					// Online retrieve quote
					{
						path: PATH_ONLINE.retrieveQuote,
						element: <StaticPageLayout />,
						children: [{ element: <RetrieveQuoteOnline />, index: true }],
					},

					// Main Routes
					{
						path: "*",
						element: <NoHeaderLayout />,
						children: [
							{ path: "404", element: <NotFound /> },
							{ path: "*", element: <Navigate to="/404" replace /> },
						],
					},
					{ path: "*", element: <Navigate to="/404" replace /> },
				]
	);
}

//----------------------------------------------------------------
// ** AUTHENTICATION
const Login = Loadable(LoginPage);

// ** ONLINE
const AcceptAndProceed = Loadable(AcceptAndProceedPage);
// ** ONLINE PAYMENTS
const PaymentDetailsOnline = Loadable(PaymentDetailsOnlinePage);

//** ONLINE POLICY OUTCOME
const PolicyOutcomeOnline = Loadable(PolicyOutcomeOnlinePage);

//*********************************/
// ** CONTACT CENTER
const OpeningScript = Loadable(OpeningScriptPage);
// ** CONTACT CENTER PAYMENTS
const PaymentDetailsCC = Loadable(PaymentDetailsCCPage);

const PolicyOutcomeCC = Loadable(PolicyOutcomeCCPage);

const UpgradeSaveConfirmation = Loadable(UpgradeSaveConfirmationPage);

//** CONTACT CENTER POLICY OUTCOME

//*********************************/

// ** CONTACT CENTER LANDING PAGE
const ProcessingLandingPageCC = Loadable(ProcessingLandingPageCCPage);
// ** CONTACT CENTER RETRIEVE QUOTE PAGE
const RetrieveQuotePageCC = Loadable(RetrieveQuotePageCCPage);
// ** SHARED
const YourDetailsContact = Loadable(YourDetailsContactPage);
const YourDetailsPersonal = Loadable(YourDetailsPersonalPage);
const SelectCover = Loadable(SelectCoverPage);
const QuoteSummary = Loadable(QuoteSummaryPage);

// ** SHARED PAYMENT PATH
const PaymentContactDetails = Loadable(PaymentContactDetailsPage);
const PolicyPurchaseConfirmation = Loadable(PolicyPurchaseConfirmationPage);
const AboutYouDisclosure = Loadable(AboutYouDisclosurePage);
const AboutYouQuestions = Loadable(AboutYouQuestionsPage);
const PolicyOutcomeDecline = Loadable(PolicyOutcomeDeclinePage);
const ReviewApplication = Loadable(ReviewApplicationPage);
const RetrieveLoading = Loadable(RetrieveLoadingPage);
const RetrieveDecline = Loadable(RetrieveOutcomeDeclinePage);
const RetrieveReferredUW = Loadable(RetrieveOutcomeDeclinePage);
const RetrieveExpired = Loadable(RetrieveOutcomeExpiredPage);
const RetrieveBound = Loadable(RetrieveOutcomeBoundPage);
const RetrieveQuoteOnline = Loadable(RetrieveQuoteOnlinePage);
const ApplicationAlreadyDeclined = Loadable(AlreadyDeclinedApplication);
// ** miscellaneous
const NotFound = Loadable(Page404Page);
const LandingPage = Loadable(LandingPagePage);
const LifeInfoAndDuty = Loadable(LifeInfoAndDutyPage);
const MaintenanceInfoPage = Loadable(MaintenanceInfo);
